'use client'

import { Icon } from '@/components/ui/icon'
import { ReservationFAB } from '@/components/layout/reservation-fab'
import { toggleMobileMenu, useMobileMenuOpen } from '@/components/layout/mobile-menu/mobile-menu-store'
import { CONTACT_PATHS } from '@/config/contact'
import type { IconName } from '@/config/icons'
import { useCurrentUser } from '@/hooks/use-current-user'
import { useFeatureFlags } from '@/lib/features/feature-flags-context'
import { UserRole } from '@prisma/client'
import { cn } from '@heroui/react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useRef } from 'react'

const NAV_H = 72

// ecommerceEnabled is still passed by the root layout; the Products tab it
// gated now lives in the shared menu drawer, which reads the flag from
// AppHeader. Accepted here so the layout call site stays unchanged.
export function BottomNav(_props: { ecommerceEnabled?: boolean } = {}) {
  const pathname = usePathname()
  const user = useCurrentUser()
  const navRef = useRef<HTMLDivElement>(null)
  const scrollOffset = useRef(0)

  const isActive = (href: string) =>
    href === '/' ? pathname === '/' : pathname.startsWith(href)

  // ADMIN gets the desktop-first experience with no bottom bar, and the
  // booking and checkout wizards own their own sticky action bars, so the
  // site-wide bar is suppressed on both.
  const suppressed =
    user?.role === UserRole.ADMIN ||
    pathname.startsWith('/book') ||
    pathname.startsWith('/checkout')

  // Publish the bar's geometry on <html> for everything that positions
  // against it.
  //
  // --app-bottom-nav-present (1/0) is the source of truth for
  // --app-bottom-nav-h (see globals.css). It has to follow the real render:
  // when the bar was suppressed but the height stayed 72px, the closed menu
  // sheet sat 72px above the viewport bottom and its top strip showed with
  // no bar over it, and the product sticky bar floated on the same gap.
  //
  // --app-bottom-nav-offset is the scroll-hide distance. scrollOffset
  // accumulates direction changes, clamped to [0, NAV_H]: 0 = fully visible,
  // NAV_H = fully hidden (slid into the container's bottom half). Other
  // bottom-anchored UI (ServiceStickyCTA, ProductStickyCTA, the menu sheet)
  // subtracts it so it rides down with the bar instead of leaving a gap.
  useEffect(() => {
    const root = document.documentElement
    root.style.setProperty('--app-bottom-nav-present', suppressed ? '0' : '1')
    const clear = () => {
      root.style.removeProperty('--app-bottom-nav-present')
      root.style.removeProperty('--app-bottom-nav-offset')
    }
    if (suppressed) {
      root.style.setProperty('--app-bottom-nav-offset', '0px')
      return clear
    }

    // A bar that has just come back (leaving /book, signing out of an admin
    // session) starts visible rather than inheriting the last scroll-hide.
    scrollOffset.current = 0
    let lastY = window.scrollY
    const setOffset = (offset: number) => {
      navRef.current?.style.setProperty('transform', `translateY(${offset}px)`)
      root.style.setProperty('--app-bottom-nav-offset', `${offset}px`)
    }
    setOffset(scrollOffset.current)
    const onScroll = () => {
      const y = window.scrollY
      const delta = y - lastY
      lastY = y
      // Scroll DOWN (delta > 0) → reveal nav (decrease offset)
      // Scroll UP (delta < 0) → hide nav (increase offset)
      scrollOffset.current = Math.max(0, Math.min(NAV_H, scrollOffset.current - delta))
      setOffset(scrollOffset.current)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      clear()
    }
  }, [suppressed])

  if (suppressed) {
    return null
  }

  const navClasses = cn(
    'bg-background',
    'border-t border-border shadow-[0_-1px_8px_rgba(0,0,0,0.08)]',
    'flex items-center justify-around',
    'px-1 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom,0px))]',
    'h-[72px]'
  )

  const navContent = user?.role === UserRole.INSTALLER
    ? <InstallerNav isActive={isActive} />
    : <UserNav isActive={isActive} />

  /* Scroll-hide nav: fixed container is 2× nav height. Bottom half sits
     below the viewport (clipped). Top half is visible. The nav slides
     between the halves driven by scroll delta, 1:1. */
  return (
    <div
      className='fixed z-[1000] w-full pointer-events-none overflow-hidden md:hidden left-0'
      style={{ bottom: `-${NAV_H}px`, height: `${NAV_H * 2}px` }}
    >
      <div ref={navRef} className='w-full pointer-events-auto'>
        <nav aria-label="Main navigation" className={navClasses}>
          {navContent}
        </nav>
      </div>
      <div style={{ height: `${NAV_H}px` }} aria-hidden />
    </div>
  )
}

/**
 * One nav item: icon + label, active state highlighted. The teal pairing
 * rule bans bare accent-coloured text/icons on paper (spec 3.1), so the
 * active state is a teal pill (bg-accent) with a white icon
 * (text-accent-foreground) instead of recoloured text; inactive items stay
 * plain muted ink text, no pill.
 */
function NavLink({
  href,
  icon,
  label,
  active,
}: {
  href: string
  icon: IconName
  label: string
  active: boolean
}) {
  return (
    <Link
      href={href}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex flex-col items-center gap-0.5 min-w-[44px] min-h-[44px] justify-center',
        active ? 'text-foreground' : 'text-muted'
      )}
    >
      {active ? (
        <span className='flex items-center justify-center rounded-full bg-accent text-accent-foreground px-3 py-1'>
          <Icon icon={icon} className='w-5 h-5' />
        </span>
      ) : (
        <Icon icon={icon} className='w-5 h-5' />
      )}
      <span className='text-[0.625rem] font-medium'>{label}</span>
    </Link>
  )
}

/**
 * Installer tabs: today's jobs (the schedule, where progress is updated),
 * the two-week shift roster, and the account. Schedule matches /installer
 * and the appointment detail pages under it; Shifts is its own route.
 */
function InstallerNav({ isActive }: { isActive: (href: string) => boolean }) {
  const pathname = usePathname()
  const shiftsActive = pathname.startsWith('/installer/shifts')
  return (
    <>
      <NavLink href='/installer' icon='calendar' label='Schedule' active={isActive('/installer') && !shiftsActive} />
      <NavLink href='/installer/shifts' icon='clock' label='Shifts' active={shiftsActive} />
      <NavLink href='/account' icon='user' label='Account' active={isActive('/account')} />
    </>
  )
}

/**
 * Phone tabs: the three sections prospects browse, the booking entry, the
 * quote form, and Menu for everything else (Wraps, Fleet, Colours, Articles,
 * Contact, Products, account). Articles, Products and Account used to hold
 * the last three slots, which left the sales pages unreachable from the bar
 * and sent logged-out visitors to a login screen from "Account".
 */
function UserNav({ isActive }: { isActive: (href: string) => boolean }) {
  const menuOpen = useMobileMenuOpen()
  const { bookingEnabled, instantQuoteEnabled } = useFeatureFlags()
  // /projects 308-redirects to /gallery; light the Gallery slot for both
  const galleryActive = isActive('/gallery') || isActive('/projects')
  const quoteActive =
    isActive(CONTACT_PATHS.quote) || isActive(CONTACT_PATHS.fleetQuote) || isActive(CONTACT_PATHS.instantQuote)
  return (
    <>
      <NavLink href='/' icon='home' label='Home' active={isActive('/')} />
      <NavLink href='/services' icon='service' label='Services' active={isActive('/services')} />
      <NavLink href='/gallery' icon='gallery' label='Gallery' active={galleryActive} />

      {/* Centre slot: the booking FAB, or the instant estimator while online
          booking is off (the Quote tab beside it is the photo form). With the
          estimator off too, the slot goes to the quote form. */}
      {bookingEnabled ? (
        <ReservationFAB variant="nav" />
      ) : (
        <Link
          href={instantQuoteEnabled ? CONTACT_PATHS.instantQuote : CONTACT_PATHS.quote}
          aria-label={instantQuoteEnabled ? 'Get an instant quote' : 'Request a quote'}
          className='relative flex h-[44px] w-[44px] items-center justify-center rounded-full bg-accent text-accent-foreground transition-transform hover:bg-accent-hover active:scale-95'
        >
          <Icon icon='quote' className='w-5 h-5' />
        </Link>
      )}

      <NavLink href={CONTACT_PATHS.quote} icon='quote' label='Quote' active={quoteActive} />
      <button
        type='button'
        onClick={toggleMobileMenu}
        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={menuOpen}
        aria-controls='site-menu'
        className={cn(
          'flex flex-col items-center gap-0.5 min-w-[44px] min-h-[44px] justify-center',
          menuOpen ? 'text-foreground' : 'text-muted'
        )}
      >
        {/* No teal pill here: the page's own tab keeps the one active
            pill, and the FAB is already teal. Open state is ink on muted. */}
        <Icon icon={menuOpen ? 'cw-close' : 'menu'} className='w-5 h-5' />
        <span className='text-[0.625rem] font-medium'>Menu</span>
      </button>
    </>
  )
}
